import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { debounce } from 'lodash';
import { Collapse, Fade } from '@material-ui/core';
import { CheckCircle as CheckCircleIcon } from '@material-ui/icons';
import { validate as isValidEmail } from 'email-validator';
import { Pathname } from 'Routes';
import { Button, Snackbar } from 'Components';
import RotatingLoader from 'Components/RotatingLoader';
import { AuthContext, AppContext } from 'Context';
import { AxiosCancelToken, dataFetcher, endpoints } from 'Api';
import { Input } from '../../public/authentication/components/input-field';
import { AuthPage } from '../../public/authentication/components/page';
import styles from './index.module.css';
import authStyles from '../../public/authentication/auth.module.css';

const transitionTimeout = 300;
const defaultSnackbar = { isVisible: false, type: undefined, message: undefined };

export const ManageDevice = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoCode = searchParams.get('code');
  const isAutoMode = Boolean(autoCode);

  const { preferredEmail, setPreferredEmail } = useContext(AuthContext);
  const { langText } = useContext(AppContext);
  const manageDeviceText = langText?.manageDevice;
  const [email, setEmail] = useState(isValidEmail(preferredEmail) ? preferredEmail : '');
  const [qrCode, setQrCode] = useState(autoCode || '');
  const [qrCodeIsValid, setQrCodeValidity] = useState(Boolean(autoCode && autoCode.length >= 6));
  const [emailIsFixed, fixEmail] = useState(false);
  const [emailIsValid, setEmailValidity] = useState(false);
  const [accountExists, setAccountExistence] = useState(undefined);
  const [checkingAccountExistence, setAccountExistenceCheckingStatus] = useState(false);
  const [cancelToken, setCancelToken] = useState(undefined);
  const [autoSucceeded, setAutoSucceeded] = useState(false);
  const [autoFailed, setAutoFailed] = useState(false);
  const autoTriggeredRef = useRef(false);

  const [snackbar, setSnackbar] = useState(defaultSnackbar);

  const { isValidating: addingDevices, mutate: addDevice } = useSWR([endpoints.registerTv, qrCode], {
    fetcher: (url, inputQRCode) => dataFetcher(url, { code: inputQRCode }),
    onSuccess: ({ success, message }) => {
      if (success) {
        if (isAutoMode) {
          setAutoSucceeded(true);
          return;
        }
        setSnackbar({
          isVisible: true,
          message: 'Device Added',
        });
        setTimeout(() => { navigate(Pathname.dashboard); }, 500);
      } else {
        if (isAutoMode) setAutoFailed(true);
        setSnackbar({ isVisible: true, type: 'error', message });
      }
    },
    onError: () => {
      if (isAutoMode) setAutoFailed(true);
      setSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while sending Reset Code' });
    },
  });

  useEffect(() => {
    if (!isAutoMode || autoTriggeredRef.current) return;
    autoTriggeredRef.current = true;
    addDevice();
  }, [isAutoMode]);

  const qrCodeHandler = debounce(({ target: { value: inputQrCode } }) => {
    if (inputQrCode.length >= 6) {
      setQrCode(inputQrCode);
      setQrCodeValidity(true);
    } else {
      setQrCode('');
      setQrCodeValidity(false);
    }
  }, 750, { trailing: true });

  if (isAutoMode) {
    return (
      <AuthPage>
        {!autoSucceeded && !autoFailed && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <RotatingLoader text="Adding your device..." />
          </div>
        )}

        {autoSucceeded && (
          <div style={{ textAlign: 'center', padding: '24px 16px' }}>
            <CheckCircleIcon style={{ fontSize: 72, color: '#4CAF50', marginBottom: 16 }} />
            <h2 style={{ margin: '0 0 12px', fontFamily: 'Montserrat, sans-serif' }}>
              TV Login Success
            </h2>
            <p style={{ margin: '0 0 24px', color: '#555', fontFamily: 'Montserrat, sans-serif' }}>
              Your device has been added successfully.
            </p>
            <Button
              isBlue
              isLarge
              onClick={() => { navigate(Pathname.dashboard); }}
            >
              Go to Dashboard
            </Button>
          </div>
        )}

        {autoFailed && (
          <div style={{ textAlign: 'center', padding: '24px 16px' }}>
            <h2 style={{ margin: '0 0 12px', fontFamily: 'Montserrat, sans-serif', color: 'white' }}>
              TV Login Failed
            </h2>
            <p style={{ margin: '0 0 24px', color: '#555', fontFamily: 'Montserrat, sans-serif' }}>
              We couldn&apos;t add your device. Please try entering the code manually.
            </p>
            <Button
              isBlue
              isLarge
              onClick={() => { navigate(Pathname.tvLogin, { replace: true }); }}
            >
              Try Again
            </Button>
          </div>
        )}

        <Snackbar
          isVisible={snackbar?.isVisible}
          message={snackbar?.message || ''}
          onClose={() => { setSnackbar(defaultSnackbar); }}
          type={snackbar?.type}
        />
      </AuthPage>
    );
  }

  return (
    <AuthPage>
      <form onSubmit={(e) => { e.preventDefault(); }}>
        <Input
          defaultValue={email}
          type="code"
          // isDisabled={(email === preferredEmail) && checkingAccountExistence}
          isLoading={checkingAccountExistence}
          label="Enter TV Code"
          onChange={(e) => {
            e.persist();
            qrCodeHandler(e);
            setAccountExistenceCheckingStatus(false);
            if (cancelToken) { cancelToken.cancel(); }
            if (!e?.target?.value) {
              setEmail('');
              setAccountExistence(undefined);
              setEmailValidity(false);
            } else {
              setCancelToken(AxiosCancelToken.source());
            }
          }}
          placeholder="TV Code"
        />
        <br />
        <Button
          blockText
          isBlue
          isDisabled={!qrCodeIsValid || addingDevices}
          isFullWidth
          isLarge
          onClick={addDevice}
        >
          {addingDevices ? `${(manageDeviceText?.addingDevice || 'Adding Device')}...` : (manageDeviceText?.addDevice || 'Add Device')}
        </Button>
      </form>

      <br />
      <br />
      <br />
      <br />

      <Snackbar
        isVisible={snackbar?.isVisible}
        message={snackbar?.message || ''}
        onClose={() => { setSnackbar(defaultSnackbar); }}
        type={snackbar?.type}
      />
    </AuthPage>
  );
};
